import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import DatasetRoundedIcon from '@mui/icons-material/DatasetRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ImageSearchRoundedIcon from '@mui/icons-material/ImageSearchRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ViewModuleRoundedIcon from '@mui/icons-material/ViewModuleRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

const MainCard = styled(Card)(({ theme }) => ({
  borderRadius: 18,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(3),
}));

const Banner = styled(Box)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.32)}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  padding: theme.spacing(1.4, 1.6),
}));

const ImageCard = styled(Box)(({ theme }) => ({
  width: 154,
  borderRadius: 10,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

export default function DatasetView() {
  return {
    main: (
      <MainCard>
        <Stack spacing={2.5}>
          <Banner>
            <Stack direction="row" alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2}>
              <Stack direction="row" spacing={1.2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <AutoAwesomeRoundedIcon sx={{ color: 'primary.main', mt: { xs: 0.2, sm: 0 } }} />
                <Typography variant="body2" color="text.secondary">
                  This project is eligible for Neural Architecture Search. Train with NAS to automatically find the best model architecture for this dataset.
                </Typography>
              </Stack>
              <Button variant="outlined" size="small" sx={{ borderRadius: 2, textTransform: 'none', flexShrink: 0 }}>
                Try NAS
              </Button>
            </Stack>
          </Banner>

          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <DatasetRoundedIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: 24, md: 30 } }}>
                Dataset
              </Typography>
            </Stack>
            <Button variant="contained" endIcon={<ExpandMoreRoundedIcon />} sx={{ borderRadius: 2.5, textTransform: 'none', px: 2 }}>
              Train Model
            </Button>
          </Stack>

          <TextField
            fullWidth
            size="small"
            placeholder="Search images"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button startIcon={<SearchRoundedIcon />} sx={{ textTransform: 'none' }}>
                    Search
                  </Button>
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1.2} justifyContent="space-between">
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <TextField size="small" placeholder="Filter by filename" sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              <Select size="small" defaultValue="split" sx={{ minWidth: 96, borderRadius: 2 }}>
                <MenuItem value="split">Split</MenuItem>
              </Select>
              <Select size="small" defaultValue="classes" sx={{ minWidth: 96, borderRadius: 2 }}>
                <MenuItem value="classes">Classes</MenuItem>
              </Select>
              <Select size="small" defaultValue="tags" sx={{ minWidth: 84, borderRadius: 2 }}>
                <MenuItem value="tags">Tags</MenuItem>
              </Select>
              <Select size="small" defaultValue="newest" sx={{ minWidth: 150, borderRadius: 2 }}>
                <MenuItem value="newest">Sort By Newest</MenuItem>
              </Select>
              <Button variant="outlined" startIcon={<ImageSearchRoundedIcon />} sx={{ borderRadius: 2, textTransform: 'none' }}>
                Search by Image
              </Button>
            </Stack>
            <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon />} sx={{ borderRadius: 2, textTransform: 'none' }}>
              Export
            </Button>
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              0 images selected
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <FormControlLabel control={<Switch size="small" defaultChecked />} label="Show annotations" sx={{ m: 0 }} />
              <Stack direction="row" spacing={0.75}>
                <IconButton sx={{ width: 34, height: 34, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <ViewListRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton sx={{ width: 34, height: 34, border: '1px solid', borderColor: 'primary.main', borderRadius: 2, color: 'primary.main' }}>
                  <ViewModuleRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Stack>
          </Stack>

          <Box sx={{ minHeight: 420 }}>
            <ImageCard>
              <Box component="img" src="/static/demo4.png" alt="dataset item" sx={{ display: 'block', width: '100%', height: 112, objectFit: 'cover' }} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, py: 1 }} noWrap>
                10439_car_0_20260415_170234.jpg
              </Typography>
            </ImageCard>
          </Box>

          <Divider />

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Select size="small" defaultValue="50" sx={{ minWidth: 92, borderRadius: 2 }}>
              <MenuItem value="50">Images per page: 50</MenuItem>
            </Select>
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                <ChevronLeftRoundedIcon fontSize="small" />
              </IconButton>
              <Typography variant="caption" color="text.secondary">
                1 - 1 of 1
              </Typography>
              <IconButton size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                <ChevronRightRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Stack>
      </MainCard>
    ),
    side: null,
  };
}
